import {
  type FC,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useGetGameBoardQuery } from '@/api/queries/game';
import {
  type ActiveTileRef,
  type MiniGameType,
} from '@/common/types/dice-game';
import { setActiveBoard } from '@/redux/reducers/diceGameReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { useDiceBoardData } from './hooks/dice-board-data';
import { DiceGameContext } from './context';
import { getActiveBoard } from './lib';

const DiceGameProvider: FC<PropsWithChildren> = ({ children }) => {
  const diceBoardData = useDiceBoardData();
  const activeBoard = useAppSelector((state) => state.diceGame.activeBoard);
  const activeTileRef = useRef<ActiveTileRef>(null);
  const [diceFromRef, setDiceFromRef] =
    useState<RefObject<HTMLImageElement> | null>(null);
  const [energyFromRef, setEnergyFromRef] =
    useState<RefObject<HTMLImageElement> | null>(null);
  const [energyToRef, setEnergyToRef] =
    useState<RefObject<HTMLImageElement> | null>(null);
  const [diceToRef, setDiceToRef] =
    useState<RefObject<HTMLImageElement> | null>(null);
  const [cardsRewardFromRef, setCardsRewardFromRef] =
    useState<RefObject<HTMLElement> | null>(null);
  const [isFlyingCardsReward, setIsFlyingCardsReward] =
    useState<MiniGameType | null>(null);
  const [flyingAnimation, setFlyingAnimation] = useState<
    'tgem' | 'energy' | 'dice' | 'star' | null
  >(null);
  const [rewardScaleQueue, setRewardScaleQueue] = useState<number[]>([]);
  const dispatch = useAppDispatch();

  const { data } = useGetGameBoardQuery();

  useEffect(() => {
    if (!data) return;

    if ('board' in data) {
      const activeBoard = getActiveBoard(data.miniGame);

      dispatch(setActiveBoard(activeBoard));
    }
  }, [data]);

  const setIsRewardScaled = useCallback(
    (value: boolean) => {
      if (value) {
        setRewardScaleQueue((prev) => [...prev, 1]);
      } else {
        setRewardScaleQueue((prev) => prev.slice(1));
      }
    },
    [setRewardScaleQueue],
  );
  const isRewardScaled = rewardScaleQueue.length > 0;

  useEffect(() => {
    setRewardScaleQueue([]);
  }, [activeBoard]);

  return (
    <DiceGameContext.Provider
      value={{
        dice: diceBoardData,
        activeTileRef,
        // miniGame: miniGameBoardData,
        diceToRef,
        setDiceToRef,
        diceFromRef,
        setDiceFromRef,
        flyingAnimation,
        setFlyingAnimation,
        energyToRef,
        setEnergyToRef,
        setEnergyFromRef,
        energyFromRef,
        cardsRewardFromRef,
        setCardsRewardFromRef,
        isFlyingCardsReward,
        setIsFlyingCardsReward,
        isRewardScaled,
        setIsRewardScaled,
      }}
    >
      {children}
    </DiceGameContext.Provider>
  );
};

export { DiceGameProvider };
